import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  Alert,
  Button,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  AccountBalance as AccountBalanceIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';

const Notifications = ({ onNotificationsViewed }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const markAllAsViewed = (notificationIds) => {
    if (user?.userId && notificationIds && notificationIds.length > 0) {
      const viewedNotifications = JSON.parse(localStorage.getItem(`viewedNotifications_${user.userId}`) || '[]');
      const newViewed = [...new Set([...viewedNotifications, ...notificationIds])];
      localStorage.setItem(`viewedNotifications_${user.userId}`, JSON.stringify(newViewed));
      if (onNotificationsViewed) {
        onNotificationsViewed();
      }
    }
  };

  const markAsViewed = (notificationId) => {
    if (user?.userId) {
      const viewedNotifications = JSON.parse(localStorage.getItem(`viewedNotifications_${user.userId}`) || '[]');
      if (!viewedNotifications.includes(notificationId)) {
        viewedNotifications.push(notificationId);
        localStorage.setItem(`viewedNotifications_${user.userId}`, JSON.stringify(viewedNotifications));
        if (onNotificationsViewed) {
          onNotificationsViewed();
        }
      }
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const [loansResponse, requestsResponse, closedLoansResponse] = await Promise.all([
        api.get('/officer/loans', { params: { officerId: user.userId } }),
        api.get('/officer/customer-requests', { params: { officerId: user.userId } }),
        api.get('/officer/loans/closed-by-admin', { params: { officerId: user.userId } }).catch(() => ({ data: [] })),
      ]);
      
      const loans = loansResponse.data || [];
      const requests = requestsResponse.data || [];
      const closedLoans = closedLoansResponse.data || [];
      
      // Get viewed notifications from localStorage
      const viewedNotifications = JSON.parse(localStorage.getItem(`viewedNotifications_${user.userId}`) || '[]');
      
      // Create notifications from loans closed by admin (recent ones)
      const closedLoanNotifications = closedLoans
        .filter(loan => {
          const isRecent = loan.closedAt && 
            new Date(loan.closedAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000); // Last 7 days
          const isViewed = viewedNotifications.includes(`admin-closed-loan-${loan.id}`);
          return isRecent && !isViewed;
        })
        .map(loan => ({
          id: `admin-closed-loan-${loan.id}`,
          type: 'admin-closed-loan',
          title: 'Loan Closed by Admin',
          message: `Loan #${loan.id} for ${loan.customer?.fullName || 'customer'} was closed by admin${loan.arrearsAmount && loan.arrearsAmount > 0 ? ` with Rs ${loan.arrearsAmount.toFixed(2)} arrears deducted` : ''}`,
          timestamp: loan.closedAt,
          loanId: loan.id,
          severity: loan.arrearsAmount && loan.arrearsAmount > 0 ? 'warning' : 'info',
          isNew: true,
        }));
      
      // Create notifications from loans
      const loanNotifications = loans
        .filter(loan => 
          loan.status === 'ACTIVE' || 
          loan.status === 'APPROVED' || 
          loan.status === 'REJECTED'
        )
        .map(loan => {
          const notificationId = `loan-${loan.id}`;
          return {
            id: notificationId,
            type: 'loan',
            status: loan.status,
            title: `Loan Application #${loan.id}`,
            message: loan.status === 'ACTIVE' || loan.status === 'APPROVED'
              ? `Your loan application for ${loan.customer?.fullName || loan.customer?.name} has been approved!`
              : loan.status === 'REJECTED'
              ? `Your loan application for ${loan.customer?.fullName || loan.customer?.name} has been rejected.`
              : '',
            customerName: loan.customer?.fullName || loan.customer?.name,
            amount: loan.requestedAmount,
            date: loan.updatedAt || loan.createdAt,
            loanId: loan.id,
            isNew: !viewedNotifications.includes(notificationId),
          };
        });
      
      // Create notifications from customer requests
      const requestNotifications = requests
        .filter(request => 
          request.status === 'APPROVED' || 
          request.status === 'REJECTED' ||
          request.status === 'APPROVED_FOR_EDIT'
        )
        .map(request => {
          const notificationId = `request-${request.id}`;
          return {
            id: notificationId,
            type: 'customer_request',
            status: request.status,
            title: `Customer ${request.requestType} Request #${request.id}`,
            message: request.status === 'APPROVED'
              ? `Your ${request.requestType.toLowerCase()} request for ${request.customer?.fullName || request.customer?.name} has been approved.`
              : request.status === 'APPROVED_FOR_EDIT'
              ? `Your update request for ${request.customer?.fullName || request.customer?.name} has been approved with field restrictions. You can now edit the allowed fields.`
              : request.status === 'REJECTED'
              ? `Your ${request.requestType.toLowerCase()} request for ${request.customer?.fullName || request.customer?.name} has been rejected. ${request.adminComments ? `Reason: ${request.adminComments}` : ''}`
              : '',
            customerName: request.customer?.fullName || request.customer?.name,
            date: request.reviewedAt || request.requestedAt,
            requestId: request.id,
            requestType: request.requestType,
            adminComments: request.adminComments,
            allowedFields: request.allowedFields,
            isNew: !viewedNotifications.includes(notificationId),
          };
        });
      
      // Combine and sort by date (newest first)
      const allNotifications = [...closedLoanNotifications, ...loanNotifications, ...requestNotifications]
        .sort((a, b) => new Date(b.timestamp || b.date || 0) - new Date(a.timestamp || a.date || 0));
      
      setNotifications(allNotifications);
      
      // Mark all as viewed after fetching (when page is opened)
      const notificationIds = allNotifications.map(n => n.id);
      markAllAsViewed(notificationIds);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoanClick = (loanId) => {
    navigate(`/officer/loans/${loanId}/schedule`);
  };

  const handleRequestClick = (requestId, requestType, status) => {
    if (status === 'APPROVED_FOR_EDIT') {
      // Navigate to customer edit page
      api.get(`/officer/customer-requests`, { params: { officerId: user.userId } })
        .then(response => {
          const request = response.data.find(r => r.id === requestId);
          if (request && request.customer) {
            navigate(`/officer/customers/${request.customer.id}`);
          }
        });
    } else {
      // Navigate to customer management
      api.get(`/officer/customer-requests`, { params: { officerId: user.userId } })
        .then(response => {
          const request = response.data.find(r => r.id === requestId);
          if (request && request.customer) {
            navigate(`/officer/customers/${request.customer.id}`);
          }
        });
    }
  };

  const getNotificationIcon = (type, status, severity) => {
    if (type === 'admin-closed-loan') {
      return <AccountBalanceIcon color={severity === 'warning' ? 'warning' : 'info'} />;
    } else if (type === 'loan') {
      if (status === 'APPROVED' || status === 'ACTIVE') {
        return <CheckCircleIcon color="success" />;
      } else if (status === 'REJECTED') {
        return <CancelIcon color="error" />;
      }
    } else if (type === 'customer_request') {
      if (status === 'APPROVED' || status === 'APPROVED_FOR_EDIT') {
        return <CheckCircleIcon color="success" />;
      } else if (status === 'REJECTED') {
        return <CancelIcon color="error" />;
      }
    }
    return <InfoIcon color="info" />;
  };

  const getNotificationColor = (status) => {
    if (status === 'APPROVED' || status === 'ACTIVE' || status === 'APPROVED_FOR_EDIT') {
      return 'success';
    } else if (status === 'REJECTED') {
      return 'error';
    }
    return 'default';
  };

  const unreadCount = notifications.filter(n => n.isNew !== false).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Notifications</Typography>
        {unreadCount > 0 && (
          <Chip 
            label={`${unreadCount} New`} 
            color="primary" 
            size="small"
          />
        )}
      </Box>

      {loading ? (
        <Typography>Loading notifications...</Typography>
      ) : notifications.length === 0 ? (
        <Alert severity="info">No notifications available.</Alert>
      ) : (
        <Paper>
          <List>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  sx={{
                    bgcolor: notification.isNew ? 'action.hover' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    // Mark notification as viewed when clicked
                    markAsViewed(notification.id);
                    
                    if (notification.type === 'admin-closed-loan') {
                      navigate('/officer/loans-closed-by-admin');
                    } else if (notification.type === 'loan') {
                      handleLoanClick(notification.loanId);
                    } else {
                      handleRequestClick(
                        notification.requestId,
                        notification.requestType,
                        notification.status
                      );
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'transparent' }}>
                      {getNotificationIcon(notification.type, notification.status, notification.severity)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" component="span">
                          {notification.title}
                        </Typography>
                        {notification.status && (
                          <Chip
                            label={notification.status}
                            color={getNotificationColor(notification.status)}
                            size="small"
                          />
                        )}
                        {notification.severity && notification.type === 'admin-closed-loan' && (
                          <Chip
                            label={notification.severity === 'warning' ? 'With Arrears' : 'No Arrears'}
                            color={notification.severity}
                            size="small"
                          />
                        )}
                        {notification.isNew && (
                          <Chip label="New" color="primary" size="small" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {notification.timestamp || notification.date
                            ? new Date(notification.timestamp || notification.date).toLocaleString()
                            : 'N/A'}
                        </Typography>
                        {notification.status === 'APPROVED_FOR_EDIT' && notification.allowedFields && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            <Typography variant="caption">
                              <strong>Allowed Fields:</strong>{' '}
                              {(() => {
                                try {
                                  const fields = JSON.parse(notification.allowedFields);
                                  return fields.join(', ');
                                } catch {
                                  return 'See customer details';
                                }
                              })()}
                            </Typography>
                          </Alert>
                        )}
                        {notification.adminComments && notification.status === 'REJECTED' && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            <Typography variant="caption">
                              <strong>Admin Comments:</strong> {notification.adminComments}
                            </Typography>
                          </Alert>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default Notifications;

